import { create } from 'zustand';
import {
  addLocalStorage,
  convertNoticeCode,
  filterOrganizationsByRole,
  getUserLocalStorage,
  saveUserInfo,
} from '../helpers';
import {
  completeInvitedRegistration,
  confirmRegistrationWithCode,
  fetchInvitationOrganizationByCode,
  fetchMergedCurrentUser,
  postLoginRequest,
  postLogoutRequest,
  postRegisterRequest,
  requestPasswordReset,
  submitNewPassword,
} from '../services/appAuthApi';
import {
  deleteProjectDesignRequest,
  fetchFeed,
  fetchOrganizationProjectForUpdate,
  fetchOrganizationRolesByOrganizationId,
  fetchOrganizationsProjectsFiltered,
  fetchOrganizationsRoles,
  fetchProjectById,
  fetchProjectDesign,
  fetchProjectDesignsList,
  fetchProjectFeed,
  fetchProjectMapping,
  fetchProjectRule,
  fetchStaticFeedTypesJson,
  patchOrganizationNameRequest,
  postOrganizationRequest,
} from '../services/appOrgProjectApi';
import {
  deleteBillingPaymentMethod,
  fetchBillingMonthlyReports,
  fetchBillingPaymentMethodLink,
  fetchBillingPlansForOrganization,
  patchBillingDefaultPaymentMethod,
  patchBillingPaymentLink,
  patchCancelBillingSubscription,
  putBillingSubscriptionPlan,
} from '../services/appBillingApi';
import {
  buildFontUploadFormData,
  deleteFileRequest,
  deleteFontRequest,
  fetchOrganizationFontsData,
  postOrganizationFont,
} from '../services/appFontsFilesApi';
import {
  mergeDesignFontsIntoFontsList,
  mergeFontsFromOrganizationApi,
} from '../services/appFontsMerge';
import {
  applyDefaultPaymentMethodPatch,
  applyMonthlyReportPlanPatch,
  applySubscriptionNoticePatch,
  replaceOrganizationPaymentMethods,
} from '../services/appBillingState';
import useSessionStore from './useSessionStore';
import useShellStore from './useShellStore';
import { getAntdMessage } from '../utils/AntdAppMessageBinder';

function safeParseValidationLogJson(raw) {
  if (raw == null || raw === '') return {};
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
}

function parseProjectOutputs(projects) {
  return projects.map((project) => {
    if (!project.outputs?.length) return project;
    return {
      ...project,
      outputs: project.outputs.map((log) => ({
        ...log,
        validation_log_json: safeParseValidationLogJson(log.validation_log_json),
      })),
    };
  });
}

const workspaceInitialState = {
  fonts: [],
  designFonts: [],
  organizations: [],
  organizationsByProjects: [],
  organizationsWithFilteredProjects: [],
  selectedOrganization: null,
  selectedOrganizationsList: [],
  selectedProjects: [],
  selectedProjectId: null,
  billingPlans: [],
  loadingBillingPlans: false,
  loadingOrganizations: false,
  loadingProjects: false,
  loadingOneProject: false,
  projects: [],
  isAddingNewOrganization: false,
  isAddingNewProject: false,
  paymentsData: [],
};

const useWorkspaceStore = create((set, get) => ({
  ...workspaceInitialState,

  resetWorkspace: () => set({ ...workspaceInitialState }),

  setUserAuth: (data) => {
    const userTokens = {
      refresh: data.refresh,
      access: data.access,
    };
    addLocalStorage(userTokens);
    useSessionStore.getState().setUser(data.user ?? null);
  },

  getInvitationCode: (code) => fetchInvitationOrganizationByCode(code),

  finishRegistrationForInvitedUser: (requestBody, setError, history) =>
    completeInvitedRegistration(requestBody, setError, history),

  confirmRegistration: (code, setLoading) =>
    confirmRegistrationWithCode(code, setLoading),

  logIn: async (requestData, history) => {
    try {
      const data = await postLoginRequest(requestData);
      if (data) {
        saveUserInfo(data);
        await get().checkUserAuthAndOrganizations();
        const { pathname } = window.location;
        if (pathname === '/login') {
          history.push('/');
        }
        getAntdMessage().success({
          content: 'Success login!',
          duration: 3,
        });
      }
    } catch (error) {
      if (error.isAxiosError) {
        const detail =
          error.response?.data?.detail ??
          error.response?.data?.message ??
          error.message ??
          'Login failed';
        getAntdMessage().warning({
          content: typeof detail === 'string' ? detail : String(detail),
          duration: 3,
        });
      } else {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(msg);
      }
    }
  },

  logOut: async () => {
    try {
      const status = await postLogoutRequest();
      if (status === 200) {
        get().deleteLocalStorageValue('tokens');
        get().deleteLocalStorageValue('userInfo');
        useSessionStore.getState().setUser(null);
        get().resetWorkspace();
        getAntdMessage().success({
          content: 'You have been successfully logged out',
          duration: 3,
        });
        return;
      }
      throw new Error(`Logout failed with HTTP status ${status}`);
    } catch (error) {
      getAntdMessage().error(
        'Something went wrong! Please try again in a while',
      );
      const msg = error instanceof Error ? error.message : String(error);
      throw new Error(msg);
    }
  },

  deleteLocalStorageValue: (key) => {
    window.localStorage.removeItem(key);
  },

  register: async (requestBody, setError, history) => {
    try {
      const data = await postRegisterRequest(requestBody);
      saveUserInfo(data);
      await get().checkUserAuthAndOrganizations();
      getAntdMessage().success({
        content: 'Success!',
        duration: 3,
      });
      return true;
    } catch (error) {
      if (error.isAxiosError) {
        const payload = error.response?.data;
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
          const errorFields = {};
          Object.keys(payload).forEach((key) => {
            const val = payload[key];
            if (
              key === 'organization' &&
              val &&
              typeof val === 'object' &&
              Array.isArray(val.name)
            ) {
              errorFields.organizationName = val.name[0];
            } else if (Array.isArray(val) && val.length) {
              errorFields[key] = val[0];
            }
          });
          setError(errorFields);
          if (error.response?.status === 500) {
            getAntdMessage().warning(
              'Registration finished with error! Try again in a while.',
            );
          }
          return false;
        }
        getAntdMessage().warning(
          'Registration finished with error! Try again in a while.',
        );
        return false;
      }
      const msg = error instanceof Error ? error.message : String(error);
      throw new Error(msg);
    }
  },

  resetPassword: (email, setError, setLoading) =>
    requestPasswordReset(email, setError, setLoading),

  setNewPassword: (code, requestBody, setError, setLoading) =>
    submitNewPassword(code, requestBody, setError, setLoading),

  getUserInfo: () => fetchMergedCurrentUser(),

  checkUserAuthAndOrganizations: async () => {
    try {
      const { csrf, refresh } = getUserLocalStorage();
      if (csrf && refresh) {
        const { user } = await get().getUserInfo();
        useSessionStore.getState().setUser(user);
      }
    } catch (e) {
      useSessionStore.getState().setUser(null);
    }
  },

  fetchProject: async (project_id) => {
    const data = await fetchProjectById(project_id);
    if (data) {
      set({ selectedProjectId: data?.id });
    }
    return data;
  },

  fetchFeed: (project_id, feed_id) =>
    fetchProjectFeed(project_id, feed_id),

  fetchRule: (project_id, rule_id) =>
    fetchProjectRule(project_id, rule_id),

  fetchMapping: (project_id, mapping_id) =>
    fetchProjectMapping(project_id, mapping_id),

  fetchFeedTypes: () => fetchStaticFeedTypesJson(),

  fetchDesign: (project_id, design_id) =>
    fetchProjectDesign(project_id, design_id),

  fetchDesigns: (project_id) => fetchProjectDesignsList(project_id),

  deleteDesign: (project_id, id) =>
    deleteProjectDesignRequest(project_id, id),

  getFonts: async (organizationId) => {
    const data = await fetchOrganizationFontsData(organizationId);
    const filteredFonts = mergeFontsFromOrganizationApi(
      data,
      get().designFonts,
    );
    if (filteredFonts === null) return null;
    set({ fonts: filteredFonts });
    return filteredFonts;
  },

  setDesignFont: (designFonts) => {
    if (!designFonts?.length) {
      set({ designFonts: [] });
      return;
    }
    const mergedFonts = mergeDesignFontsIntoFontsList(
      designFonts,
      get().fonts,
    );
    set({
      designFonts,
      fonts: mergedFonts,
    });
  },

  uploadFont: async (file, organizationId) => {
    try {
      if (get().fonts.some((font) => font.name === file.name)) {
        getAntdMessage().warning('This font is already uploaded');
        return;
      }
      const formData = buildFontUploadFormData(file, organizationId);
      await postOrganizationFont(formData);
      const fonts = await get().getFonts(organizationId);
      return fonts?.find((font) => font.name === file.name);
    } catch (error) {
      getAntdMessage().error(error.message);
    }
  },

  deleteFont: async (id, organizationId) => {
    try {
      await deleteFontRequest(id);
      const user = useSessionStore.getState().user;
      if (user?.fonts) {
        useSessionStore.getState().setUser({
          ...user,
          fonts: user.fonts.filter((f) => f.id !== id),
        });
      }
      if (organizationId) {
        await get().getFonts(organizationId);
      }
    } catch (error) {
      getAntdMessage().error(error.message);
    }
  },

  deleteFile: async (id) => {
    try {
      await deleteFileRequest(id);
    } catch (error) {
      getAntdMessage().error(error.message);
    }
  },

  getOrganizations: async () => {
    try {
      set({
        loadingOrganizations: true,
        organizations: [],
        organizationsByProjects: [],
        paymentsData: [],
      });
      const data = await fetchOrganizationsRoles();
      if (!data) return;

      const paymentsData = await get().getPaymentsData();
      const sessionUser = useSessionStore.getState().user;
      const roles = sessionUser?.roles ?? [];
      let filteredOrganizations = filterOrganizationsByRole(data, roles);

      if (paymentsData) {
        filteredOrganizations = filteredOrganizations.map((item) => {
          const paymentInfo = paymentsData.find((d) => d.id === item.id);
          return {
            ...item,
            ...paymentInfo,
          };
        });
      }

      const isUnpaidOrganization = filteredOrganizations.some((organization) =>
        convertNoticeCode(organization.notice_code),
      );

      if (isUnpaidOrganization) {
        const defaultBillingPlans = await get().getBillingPlan();
        set({ billingPlans: defaultBillingPlans });
      }
      const organizations = filteredOrganizations.map((organization) => ({
        id: organization.id,
        key: organization.id,
        name: organization.name,
        projects: organization.projects,
      }));
      set({
        organizations,
        organizationsByProjects: filteredOrganizations,
        paymentsData: paymentsData || [],
      });
    } catch (error) {
      if (error.isAxiosError) {
        return getAntdMessage().warning({
          content: error.message,
          duration: 3,
        });
      }
      const msg = error instanceof Error ? error.message : String(error);
      throw new Error(msg);
    } finally {
      set({ loadingOrganizations: false });
    }
  },

  getOneOrganization: (organizationId) =>
    fetchOrganizationRolesByOrganizationId(organizationId),

  getOrganizationsWithFilteredProjects: async (organizationId) => {
    try {
      set({
        loadingOrganizations: true,
        selectedProjects: [],
        organizationsWithFilteredProjects: [],
      });
      const data = await fetchOrganizationsProjectsFiltered(organizationId);
      if (!data) return;

      const sessionUser = useSessionStore.getState().user;
      const userRoles = sessionUser?.roles ?? [];

      const filteredOrganizationsWithProjects = data.map((organization) => {
        const withParsed =
          organization.projects_filtered_by_id?.length > 0
            ? {
                ...organization,
                projects_filtered_by_id: parseProjectOutputs(
                  organization.projects_filtered_by_id,
                ),
              }
            : organization;
        const role = userRoles.find((r) => r.organization === organization.id);
        return {
          ...withParsed,
          generation_allowed: sessionUser?.isSuperUser
            ? true
            : organization.generation_allowed,
          role: sessionUser?.isSuperUser ? 1 : role ? role.role : null,
        };
      });

      const filteredOrganizations = filteredOrganizationsWithProjects.map(
        (organization) => ({
          id: organization.id,
          key: organization.id,
          name: organization.name,
          projects: organization.projects_filtered_by_id.map((project) => ({
            id: project.id,
            name: project.name,
          })),
        }),
      );

      set({
        organizationsWithFilteredProjects: filteredOrganizationsWithProjects,
        organizations: filteredOrganizations,
      });

      return data;
    } catch (error) {
      if (error.isAxiosError) {
        getAntdMessage().warning({
          content: error.message,
          duration: 3,
        });
      } else {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(msg);
      }
    } finally {
      set({ loadingOrganizations: false });
    }
  },

  getOneProjectByOrganizationAndUpdateState: async (
    organizationId,
    projectId,
  ) => {
    try {
      set({ loadingOneProject: true });
      const data = await fetchOrganizationProjectForUpdate(
        organizationId,
        projectId,
      );
      if (!data) return;

      const parsingProject = data.map((organization) => {
        if (organization.projects_filtered_by_id?.length) {
          return {
            ...organization,
            projects_filtered_by_id: parseProjectOutputs(
              organization.projects_filtered_by_id,
            ),
          };
        }
        return { ...organization };
      });

      const newOrg = parsingProject[0];
      if (!newOrg?.projects_filtered_by_id?.length) {
        getAntdMessage().warning({
          content:
            'Project list from the server was empty; local lists were not updated.',
          duration: 4,
        });
        return;
      }
      const updatedProject = newOrg.projects_filtered_by_id[0];

      const patchOrgList = (list) =>
        list.map((organization) => {
          if (organization.id !== newOrg.id) return organization;
          return {
            ...organization,
            projects_filtered_by_id: organization.projects_filtered_by_id.map(
              (project) =>
                project.id === updatedProject.id
                  ? { ...updatedProject }
                  : project,
            ),
          };
        });

      set({
        selectedProjects: patchOrgList(get().selectedProjects),
        organizationsWithFilteredProjects: patchOrgList(
          get().organizationsWithFilteredProjects,
        ),
      });
    } catch (error) {
      if (error.isAxiosError) {
        getAntdMessage().warning({
          content: error.message,
          duration: 3,
        });
      } else {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(msg);
      }
    } finally {
      set({ loadingOneProject: false });
    }
  },

  clearOrganizationsWithFilteredProjects: () => {
    set({ organizationsWithFilteredProjects: [] });
  },

  selectOrganization: (organization) => {
    set({ selectedOrganization: organization });
  },

  toggleIsManagementSelected: (selectedOrganization) => {
    const selectedOrganizationsList = [...get().selectedOrganizationsList];
    const updatedOrganizations = selectedOrganizationsList.map(
      (organization) => {
        if (organization.id === selectedOrganization.id) {
          return {
            ...organization,
            isManagementSelected: !organization.isManagementSelected,
          };
        }
        return organization;
      },
    );
    set({ selectedOrganizationsList: updatedOrganizations });
  },

  selectOrganizationsList: (organizations, update = false) => {
    const updateOrganizationsMembers = (
      mainOrganizationsList,
      newOrganizationsList,
    ) =>
      mainOrganizationsList.map((mainOrganization) => {
        const updatedOrganization = newOrganizationsList.find(
          (organization) => organization.id === mainOrganization.id,
        );
        if (updatedOrganization) {
          return {
            ...mainOrganization,
            members: updatedOrganization.members,
          };
        }
        return mainOrganization;
      });

    if (update) {
      const updatedOrganizations = organizations.map((organization) => ({
        ...organization,
        isManagementSelected: false,
      }));
      set({ selectedOrganizationsList: updatedOrganizations });
    } else {
      const organizationsByProjects = [...get().organizationsByProjects];
      set({
        selectedOrganizationsList: organizations,
        organizationsByProjects: updateOrganizationsMembers(
          organizationsByProjects,
          organizations,
        ),
      });
    }
  },

  selectProjectsLists: (projects) => {
    set({ selectedProjects: projects });
  },

  cancelSubscription: async (organizationId) => {
    try {
      const data = await patchCancelBillingSubscription(organizationId);
      if (!data.id) return;

      const organizationData = await get().getOneOrganization(data.id);
      if (organizationData.length === 0) return;
      const actualOrganization = organizationData[0];

      const organizationsByProjects = applySubscriptionNoticePatch(
        [...get().organizationsByProjects],
        actualOrganization,
      );
      const selectedOrganizationsList = applySubscriptionNoticePatch(
        [...get().selectedOrganizationsList],
        actualOrganization,
      );

      set({
        organizationsByProjects,
        selectedOrganizationsList,
      });
    } catch (error) {
      if (error.isAxiosError) {
        getAntdMessage().warning(
          'Canceling of the subscription finished with error! Try again in a while.',
        );
      } else {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(msg);
      }
    }
  },

  switchSubscription: async (organizationId, planId) => {
    try {
      await putBillingSubscriptionPlan(organizationId, planId);
      const monthlyReport = await get().getPaymentsData(organizationId);
      if (!monthlyReport?.length) return;
      const actualMonthlyReport = monthlyReport[0];

      const organizationsByProjects = applyMonthlyReportPlanPatch(
        [...get().organizationsByProjects],
        actualMonthlyReport.id,
        actualMonthlyReport.monthly_report.plan,
      );
      const selectedOrganizationsList = applyMonthlyReportPlanPatch(
        [...get().selectedOrganizationsList],
        actualMonthlyReport.id,
        actualMonthlyReport.monthly_report.plan,
      );

      set({
        organizationsByProjects,
        selectedOrganizationsList,
      });

      getAntdMessage().success(
        'Billing plan updated! New billing plan will commence on your next payment date.',
      );
    } catch (error) {
      if (error.isAxiosError) {
        getAntdMessage().warning(
          'Billing plan updating finished with error! Try again in a while.',
        );
      } else {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(msg);
      }
    }
  },

  getBillingPlan: (setLoading, organizationId) =>
    fetchBillingPlansForOrganization(setLoading, organizationId),

  getPaymentLink: (organizationId, pricePlan) =>
    patchBillingPaymentLink(organizationId, pricePlan),

  getLinkForAddingPaymentMethod: (organizationId) =>
    fetchBillingPaymentMethodLink(organizationId),

  changeDefaultPaymentMethod: async (
    organizationId,
    paymentMethodId,
    setLoading,
  ) => {
    try {
      setLoading(true);
      const data = await patchBillingDefaultPaymentMethod(
        organizationId,
        paymentMethodId,
      );
      if (!data) return;

      const selectedOrganizationsList = applyDefaultPaymentMethodPatch(
        [...get().selectedOrganizationsList],
        organizationId,
        data.id,
        data.default,
      );

      set({ selectedOrganizationsList });
      getAntdMessage().success('Default payment method successfully changed');
    } catch (error) {
      if (error.isAxiosError) {
        getAntdMessage().warning({
          content: error.message,
          duration: 3,
        });
      } else {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(msg);
      }
    } finally {
      setLoading(false);
    }
  },

  deletePaymentMethod: (organizationId, paymentMethodId) =>
    deleteBillingPaymentMethod(organizationId, paymentMethodId),

  onDeletingPaymentMethod: async (organizationId, paymentMethodId) => {
    await get().deletePaymentMethod(organizationId, paymentMethodId);
    const paymentData = await get().getPaymentsData(organizationId);
    if (!paymentData) return;

    const actualPaymentData = paymentData[0];

    const organizationsByProjects = replaceOrganizationPaymentMethods(
      [...get().organizationsByProjects],
      actualPaymentData,
    );
    const selectedOrganizationsList = replaceOrganizationPaymentMethods(
      [...get().selectedOrganizationsList],
      actualPaymentData,
    );

    set({
      organizationsByProjects,
      selectedOrganizationsList,
    });
  },

  getPaymentsData: (organizationId) =>
    fetchBillingMonthlyReports(organizationId),

  createOrganization: (requestBody) => postOrganizationRequest(requestBody),

  updateOrganizationName: async (id, name, setLoading) => {
    try {
      setLoading(true);
      const data = await patchOrganizationNameRequest(id, name);
      if (!data?.name) return;

      const renameInList = (arr) =>
        arr.map((obj) => (obj.id === id ? { ...obj, name: data.name } : obj));

      const renameNestedOrgs = (arr) =>
        arr.map((obj) => {
          if (obj.id !== id) return obj;
          return { ...obj, name: data.name };
        });

      set({
        selectedOrganizationsList: renameInList(get().selectedOrganizationsList),
        organizationsWithFilteredProjects: renameNestedOrgs(
          get().organizationsWithFilteredProjects,
        ),
        organizations: renameInList(get().organizations),
        organizationsByProjects: renameNestedOrgs(get().organizationsByProjects),
        selectedProjects: renameNestedOrgs(get().selectedProjects),
      });

      getAntdMessage().success(
        `Organization's name successfully changed to ${name}`,
      );
    } catch (error) {
      if (error.isAxiosError) {
        getAntdMessage().warning({
          content: `Something went wrong! ${error.message}`,
          duration: 3,
        });
      } else {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(msg);
      }
    } finally {
      setLoading(false);
    }
  },

  getProductById: async (feedId, productId, setLoading, searchField, searchValue) => {
    try {
      setLoading(true);
      const data = await fetchFeed({
        feedId,
        pageNum: 0,
        productId,
        searchField,
        searchValue,
      });
      if (!data?.products?.length) return null;
      return data.products[0];
    } catch (error) {
      if (error?.isAxiosError) {
        getAntdMessage().warning({
          content: error.message,
          duration: 3,
        });
      } else {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  },

  onAddingOrganizationClick: () => {
    if (get().isAddingNewOrganization) {
      getAntdMessage().info({
        content:
          'You have already started adding a new organization, please complete it before adding another one',
        duration: 5,
      });
    }
    set({ isAddingNewOrganization: true });
  },

  onAddingOrganizationFinish: () => {
    set({ isAddingNewOrganization: false });
  },

  onAddingProjectClick: () => {
    if (get().isAddingNewProject) {
      getAntdMessage().info({
        content:
          'You have already started adding a new project, please complete it before adding another one',
        duration: 5,
      });
    }
    set({ isAddingNewProject: true });
  },

  onAddingProjectFinish: () => {
    set({ isAddingNewProject: false });
  },

  handleSwitchMenuSelect: (page) => {
    useShellStore.getState().setCurrentPage(page);
  },

  setOrganizationsLoading: (value) => {
    set({ loadingOrganizations: value });
  },

  onCollapse: (collapsed) => {
    useShellStore.getState().setCollapsed(collapsed);
  },
}));

export default useWorkspaceStore;
